import React from 'react';
import classNames from 'classnames';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  getDeviceValuesByPageId,
  getDeviceOptionByType,
  getPlatformCascaderOptions,
  getMetricsTablePlatformsCascaderOptions,
} from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import {
  getSourceMetricsByValue,
  getMetricOptionByValue,
  DEFAULTKPITRENDSSHOWRTLIST,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import { useIntl } from 'umi';
import TipContext from '@home/components/TipContextComponents/TipContext';
import { getOptionText } from '@intelligence/components/Form/components/common/Cascader';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { IShortcutItem } from '@intelligence/components/Form/type';
import useConfigureTexts from '@intelligence/hooks/useConfigureTexts';
import { SEGMENT_CASCADER_ALL_VALUE } from '@intelligence/_config/components/filters/segmentCascader';
import { createPlatformCascaderDisplayRenderConfig } from '@intelligence/_config/components/filters/platformCascaderDisplayRender';
import RangeHourPicker from '@/pages/intelligence/components/RangeHourPicker';
import {
  KPI_TRENDS_HOURLY_CCU_METRIC,
  KPI_TRENDS_HOURLY_SOURCE_METRICS,
  KPI_TRENDS_HOURLY_FILTER_KEY_LIST,
  isKpiTrendsHourlyDateType,
  isKpiTrendsHourlyGranularity,
  buildHourlyMetricSelectionValue,
  buildKpiTrendsHourFilterValuePatch,
  formatKpiTrendsHourlyDateTime,
  getKpiTrendsHourShortcuts,
  clampKpiTrendsHourlyRange,
  getKpiTrendsHourRangePickerOption,
  normalizeKpiTrendsHourRangeValue,
} from './hourSegmentUtils';

export {
  KPI_TRENDS_HOURLY_CCU_METRIC,
  KPI_TRENDS_HOURLY_SOURCE_METRICS,
  KPI_TRENDS_HOURLY_FILTER_KEY_LIST,
  isKpiTrendsHourlyDateType,
  isKpiTrendsHourlyGranularity,
  buildHourlyMetricSelectionValue,
  buildKpiTrendsHourFilterValuePatch,
  formatKpiTrendsHourlyDateTime,
  getKpiTrendsHourShortcuts,
  clampKpiTrendsHourlyRange,
  getKpiTrendsHourRangePickerOption,
  normalizeKpiTrendsHourRangeValue,
};

const entityType = 'combine';

/** KPI Trends Hour segment 专用 RangeHourPicker customRender（FilterList 仅透传 customOption.hour） */
export const renderKpiTrendsHourRangePicker = (pickerProps: any) => (
  <RangeHourPicker
    {...pickerProps}
    showRangePrefix={false}
    className={classNames('search-border', pickerProps.className)}
  />
);

export const DATABRAIN_CALIBRATION_METRICS: string[] = [
  'databrain__total_revenue',
  'databrain__lifetime_total_revenue',
  'databrain__base_game_sales_units',
  'databrain__lifetime_base_game_sales_units',
];

const DATABRAIN_VISUALIS_REVENUE_JUMP_METRICS = [
  // Lifetime Total Revenue($)
  'databrain__lifetime_total_revenue',
  // MScience Lifetime Digital Revenue($)
  'mscienceLifetimeDigitalRevenue',
];

const DATABRAIN_VISUALIS_UNITS_JUMP_METRICS = [
  // Lifetime Base Game Sales Units
  'databrain__lifetime_base_game_sales_units',
  // Mscience Lifetime Digital Units
  'mscienceLifetimeDigitalUnits',
];

const DATABRAIN_REVENUE_JUMP_METRICS = [
  // Total Revenue($)
  'databrain__total_revenue',
  // Lifetime Total Revenue($)
  'databrain__lifetime_total_revenue',
  // MScience Lifetime Digital Revenue($)
  'mscienceLifetimeDigitalRevenue',
];

const DATABRAIN_UNITS_JUMP_METRICS = [
  // Base Game Sales Units
  'databrain__base_game_sales_units',
  // Lifetime Base Game Sales Units
  'databrain__lifetime_base_game_sales_units',
  // Mscience Lifetime Digital Units
  'mscienceLifetimeDigitalUnits',
];

// DataBrain指标跳转映射配置（基础配置，echarts勾选指标）
export const DATABRAIN_VISUALIS_METRIC_BASE_MAPPING: IRecord<string[]> = {
  // Total Revenue($) 对应的两个指标
  databrain__total_revenue: DATABRAIN_VISUALIS_REVENUE_JUMP_METRICS,
  // Lifetime Total Revenue($) 对应的两个指标
  databrain__lifetime_total_revenue: DATABRAIN_VISUALIS_REVENUE_JUMP_METRICS,
  // Base Game Sales Units 对应的两个指标
  databrain__base_game_sales_units: DATABRAIN_VISUALIS_UNITS_JUMP_METRICS,
  // Lifetime Base Game Sales Units 对应的两个指标
  databrain__lifetime_base_game_sales_units: DATABRAIN_VISUALIS_UNITS_JUMP_METRICS,
};

// DataBrain指标跳转映射配置（基础配置，指标宽表）
export const DATABRAIN_TABLE_METRIC_BASE_MAPPING: IRecord<string[]> = {
  // Total Revenue($) 对应的三个指标
  databrain__total_revenue: DATABRAIN_REVENUE_JUMP_METRICS,
  // Lifetime Total Revenue($) 对应的三个指标
  databrain__lifetime_total_revenue: DATABRAIN_REVENUE_JUMP_METRICS,
  // Base Game Sales Units 对应的三个指标
  databrain__base_game_sales_units: DATABRAIN_UNITS_JUMP_METRICS,
  // Lifetime Base Game Sales Units 对应的三个指标
  databrain__lifetime_base_game_sales_units: DATABRAIN_UNITS_JUMP_METRICS,
};

// DataBrain指标跳转时Segment of Sales筛选器勾选选项映射配置（指标宽表公用）
export const DATABRAIN_UNITS_METRICS_SALES_SEGMENT_MAPPING: IRecord<IRecord<string[]>> = {
  // Total Revenue($) 对应的两个指标
  databrain__total_revenue: SEGMENT_CASCADER_ALL_VALUE,
  // Lifetime Total Revenue($) 对应的两个指标（与Total Revenue相同）
  databrain__lifetime_total_revenue: SEGMENT_CASCADER_ALL_VALUE,
  // Base Game Sales Units 对应的两个指标
  databrain__base_game_sales_units: {
    mscience: ['Full Game'],
    gsd: [],
  },
  // Lifetime Base Game Sales Units 对应的两个指标（与Base Game Sales Units相同）
  databrain__lifetime_base_game_sales_units: {
    mscience: ['Full Game'],
    gsd: [],
  },
};

// VisualMetricSelectorDual 默认选择映射配置（echarts图表使用）
export const VISUAL_METRIC_DEFAULT_MAPPING: IRecord<string[]> = {
  ...DATABRAIN_VISUALIS_METRIC_BASE_MAPPING,
};

// DataBrain指标跳转映射配置（指标宽表使用）
export const DATABRAIN_METRIC_MAPPING: IRecord<string[]> = {
  ...DATABRAIN_TABLE_METRIC_BASE_MAPPING,
};

const platformCascaderDefaultValueNull = {
  mscience: [], // 'PC', 'PlayStation', 'Xbox', 'Nintendo'
  gsd: [], // 'PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch'
  vginsights: [], // 'PC'
  npd: [], // 'PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch', 'WII U'
  ampere: [], // 'PC', 'PlayStation', 'Xbox'
  alinea: [], // 'PC', 'PlayStation', 'Xbox'
  newzoo: [], // 'PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch'
};

// 筛选器value默认值
export const DEFAULTFILTERVALUE = {
  [filterKeyMap.MY_FILTER]: {
    value: '',
  },
  [filterKeyMap.RANGE_TIME]: {
    type: 'month',
    value: ['', ''],
  },
  [filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]: {
    value: {
      type: 'monthly',
      types: 'all',
      metrics: DEFAULTKPITRENDSSHOWRTLIST,
      sources: ['all'],
      source_metrics: getSourceMetricsByValue(DEFAULTKPITRENDSSHOWRTLIST),
      metricsOptions: getMetricOptionByValue(DEFAULTKPITRENDSSHOWRTLIST),
      selectedMetricsValue: 'metrics',
      shotSelectValue: 'all',
      selectCutVal: 'hot',
    },
  },
  [filterKeyMap.REGION]: {
    regions: ['global'],
    markets: ['global'],
    type: 'market',
  },
  [filterKeyMap.METRICTABLE_DEVICE]: {
    value: getDeviceValuesByPageId(TopchartPageId.PCConsoleMetricsTable),
  },
  [filterKeyMap.DIGITAL_SEGMENT_MULTIPLE]: {
    value: ['All'],
  },
  [filterKeyMap.CASCADER]: {
    value: SEGMENT_CASCADER_ALL_VALUE,
  },
  [filterKeyMap.PLATFORMCASCADER]: {
    value: platformCascaderDefaultValueNull,
  },
  [filterKeyMap.PUBLISHER]: {
    value: 'NETWORK',
  },
};

// 筛选器自定义布局列表
export const FILTERKEYLIST = [
  filterKeyMap.ROW,
  filterKeyMap.MY_FILTER,
  filterKeyMap.RANGE_TIME,
  filterKeyMap.SOURCE_EXPLANATION,
  filterKeyMap.ROW,
  filterKeyMap.METRIC_SELECT_FOR_KPI_TREND,
  filterKeyMap.ROW,
  filterKeyMap.REGION,
  filterKeyMap.METRICTABLE_DEVICE,
  // filterKeyMap.DIGITAL_SEGMENT_MULTIPLE,
  filterKeyMap.SAVE_FILTER_SETTINGS,
  filterKeyMap.KPITRENDS_REASE,
  filterKeyMap.CASCADER,
];

// 筛选器list配置
export const DEFAULTINPUTLIST = [
  {
    savePopupVisble: 0,
    key: filterKeyMap.ROW,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.MY_FILTER,
    value: '',
  },
  {
    savePopupVisble: 1,
    type: filterKeyMap.RANGE_TIME,
    key: filterKeyMap.RANGE_TIME,
    showSelectType: false,
    showShortcut: FilterTimeType.commonRange,
    rangeTimeOtherStyle: true,
    isShowAlloftime: false,
    // 仅 KPI Trends：外部 Segment 增加 Hour；Day/Week/Month 行为不变
    enableHourSegment: true,
    rangeTimeWrapperClassName: 'dbi-kpi-trends-hour-range-time',
    customOption: {
      hour: {
        ...getKpiTrendsHourRangePickerOption(),
        customRender: renderKpiTrendsHourRangePicker,
      },
    },
    normalizeRangeValue: normalizeKpiTrendsHourRangeValue,
  },
  {
    key: filterKeyMap.PUBLISHER,
    title: 'dbi_metric_publisher',
    default: [],
    style: { width: '250px', marginRight: '10px', marginBottom: '10px' },
    singleType: 'single',
    isShowButtonLabel: true,
    options: [
      {
        text: 'dbi_metric_publisher_All_value',
        value: 'all',
        // img: annieImg,
      },
      {
        text: 'dbi_metric_publisher_shared_value',
        value: 'NETWORK',
        // img: sensorImg,
        tip: 'dbi_metric_publisher_shared',
        tipMetrics: 'data_usage',
      },
      {
        text: 'dbi_metric_publisher_retail_value',
        value: 'RETAIL',
        // img: sensorImg,
        tip: 'dbi_metric_publisher_retail',
        tipMetrics: 'data_usage',
      },
    ],
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.DATA_SOURCE_EXPLANATION_TRIGGER,
    className: 'dbi-source-explanation--filter-right',
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.ROW,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.METRIC_SELECT_FOR_KPI_TREND,
    overlayClassName: 'dbi-kpi-trends-metric-filter-overlay dbi-metric-select-auto-scroll-overlay',
    isShowSourceTips: false,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.ROW,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.LEFT,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.REGION,
    default: [],
    visible: 1,
    disableNoChina: true,
    entityType,
    className: 'dbi-kpi-trends-range-picker',
    showRadioBubble: true,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.CASCADER,
    options: getPlatformCascaderOptions(
      { [filterKeyMap.CASCADER]: SEGMENT_CASCADER_ALL_VALUE },
      {
        customInputDisplayRender: (prop: any) => {
          const {
            isOne,
            isSingleGroupChecked,
            isGroupFullChecked,
            isShortcutGroupChecked,
            shortcutCheckedGroup,
            checkedGroup,
            isMetricChecked,
            showNum,
            showItem,
            DisplayEmpty,
            shortcutOptions,
          } = prop;
          const MAP: any = {};
          let shortcutCheckedLength: any = 0;
          shortcutCheckedGroup &&
            shortcutCheckedGroup.map((groupItem: any, index: number) => {
              shortcutCheckedLength += Object.values(groupItem.includes).flat(
                Infinity,
              ).length;
            });
          const { formatMessage } = useIntl();
          const { configureTexts } = useConfigureTexts();
          const displayShortcutLabel = shortcutOptions.map(
            (item: IShortcutItem, index: number) => {
              const trueLabel = item.labelKey
                ? formatMessage({ id: item.labelKey })
                : item.label;
              return index !== shortcutOptions.length - 1
                ? `${trueLabel}, `
                : trueLabel;
            },
          );
          return (
            <>
              <div className="dbi-cascader-select-button dbi-select-button">
                <span className="select-button-text">
                  {formatMessage({ id: 'Segment For Sales' })}
                </span>
                <div style={{ marginRight: 6, lineHeight: 0 }}>
                  <TipContext
                    Performance="left"
                    tipText={configureTexts?.segment_for_sales_select_tips}
                  />
                  {/* //  AWidth={AWidth} */}
                </div>
                {(isOne && (
                  <>
                    <div className="dbi-cascader-select-button-flex">
                      <img
                        className="dbi-cascader-common-icon"
                        src={getDataSourceIcon(showItem.parent.icon)}
                      />
                      <span>
                        {showItem.parent?.labelKey
                          ? formatMessage({ id: showItem.parent.labelKey })
                          : showItem.parent?.label}
                        -
                        {showItem.labelKey
                          ? formatMessage({ id: showItem.labelKey })
                          : showItem.label}
                      </span>
                    </div>
                  </>
                )) ||
                  (isShortcutGroupChecked && (
                    // <>
                    //   <div className="dbi-cascader-select-button-flex">
                    //     <span>{shortcutCheckedLabels}</span>
                    //   </div>
                    // </>
                    <>
                      <div className="dbi-cascader-select-button-flex">
                        <span>
                          {showItem.length > 0 &&
                          showItem.length === shortcutCheckedLength
                            ? shortcutCheckedGroup &&
                              shortcutCheckedGroup.map(
                                (item: any, i: number) => (
                                  <span key={item.value}>
                                    <img
                                      className="dbi-cascader-common-icon"
                                      src={getDataSourceIcon(MAP[item?.label])}
                                    />
                                    {getOptionText(item)}
                                    {i < shortcutCheckedGroup.length - 1
                                      ? ','
                                      : ''}
                                    &nbsp;&nbsp;
                                  </span>
                                ),
                              )
                            : showItem.length}
                        </span>
                      </div>
                    </>
                  )) ||
                  (isSingleGroupChecked && (
                    <>
                      <div className="dbi-cascader-select-button-flex">
                        <img
                          className="dbi-cascader-common-icon"
                          src={getDataSourceIcon(showItem?.icon)}
                        />
                        <span>
                          {showItem?.labelKey
                            ? formatMessage({ id: showItem?.labelKey })
                            : showItem?.label}
                        </span>
                      </div>
                    </>
                  )) ||
                  (isGroupFullChecked && (
                    <span>{formatMessage({ id: 'All' })}</span>
                  )) ||
                  (isMetricChecked && (
                    <div className="dbi-cascader-select-button-txt">
                      {showNum}
                    </div>
                  )) || <DisplayEmpty />}
              </div>
            </>
          );
        },
        customSearchItemDisplayRender: (prop: any) => {
          const {
            selectedItem,
            formatMessage,
            showLabelFormatFn: getShowLabel,
            tagAutoWidth,
            tagWidth,
            displayParentText,
          } = prop;
          return (
            <span style={{ maxWidth: tagAutoWidth ? 'unset' : tagWidth }}>
              <img
                className="dbi-cascader-common-icon"
                style={{ verticalAlign: 'middle' }}
                src={getDataSourceIcon(selectedItem.parent?.icon)}
              />
              {displayParentText
                ? `${getShowLabel(
                    selectedItem.parent || {},
                    formatMessage,
                  )} - ${getShowLabel(selectedItem, formatMessage)}`
                : getShowLabel(selectedItem, formatMessage)}
            </span>
          );
        },
        isUseClose: true,
        isNoShortcutClear: true,
        width: 360,
      },
    ),
    // AWidth: 356,
  },
  // {
  //   savePopupVisble: 1,
  //   key: filterKeyMap.DIGITAL_SEGMENT_MULTIPLE,
  //   default: DEFAULTFILTERVALUE[filterKeyMap.DIGITAL_SEGMENT_MULTIPLE],
  //   AWidth: 356,
  // },
  {
    savePopupVisble: 1,
    key: filterKeyMap.PLATFORMCASCADER,
    options: getMetricsTablePlatformsCascaderOptions(
      { [filterKeyMap.CASCADER]: platformCascaderDefaultValueNull },
      createPlatformCascaderDisplayRenderConfig({ width: 220 }),
    ),
    type: 'cascader',
    visible: 1,
    visibleinlist: 1,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.OPINION_EVENT_SETTING,
    visible: 1,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.METRICTABLE_DEVICE,
    type: filterKeyMap.CONSOLE_DEVICE,
    options: getDeviceOptionByType(TopchartPageId.PCConsoleMetricsTable),
    showIcon: true,
    visible: 2,
    btnName: 'dbi_platform',
    default: getDeviceValuesByPageId(TopchartPageId.PCConsoleMetricsTable),
    AWidth: 356,
    // tips: 'dbi_compare_pcconsole_game_filter_platform_tips',
  },

  {
    savePopupVisble: 0,
    key: filterKeyMap.SAVE_FILTER_SETTINGS,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.KPITRENDS_REASE,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.RIGHT,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.KPITRENDS_CALIBRATION_METRICS_SWITCHER,
    visible: false,
    bubblePlacement: '',
    bubbleContent: '',
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.ROW,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.DATA_SOURCE_EXPLANATION_BODY,
  },
];
